import React, { useEffect, useRef, useState } from 'react';
import { browserHistory, Link } from 'react-router';
import PropTypes from "prop-types";
import ConflictCount from '../conflict-count/conflict-count';
import InaccessibleLink from '../shim/inaccessible-link';
import util from '../util/util';

import headerLogo from './images/pbs-logo.svg';
import _ from "lodash";

export default function PageHeader (props) {
  const stationLength = 10;
  // the most common use of refs in React is:
  // 1. to create references to DOM elements and run imperative logic when needed
  //    (force elements to become focused, scroll up,...)
  // 2. to store in memory values you need to watch
  const initialTimeout = useRef(0);
  const reoccurringTimeout = useRef(0);

  // user email/name to display;
  const [user, setUser] = useState(null);
  // the user's selected station;
  const [station, setStation] = useState(null);
  // stations a user is associated with;
  const [stations, setStations] = useState([]);
  // string to parse station list with;
  const [stationSearch, setStationSearch] = useState(``);
  // security model set after user and station are defined;
  const [permissions, setPermissions] = useState({});

  // 'the function passed to useEffect will run after the render is committed
  // to the screen; think of effects as an escape hatch from React’s
  // purely functional world into the imperative world.'
  useEffect( () => {
    // get the pods transcoding status, and sends the notification on Success of Fail;
    const getPodsTranscodingNotifications = () => {
      // clear any existing timeout;
      clearTimeout(reoccurringTimeout.current);

      // leaving the pods that are still in 'processing'
      // and only sending notification for the 'ready' and 'failed' ones
      // and only do this when not on a Pod Detail screen
      if (!browserHistory.getCurrentLocation().pathname.includes("/pod/")) {
        util.getPodsNotifications().then(data => {
          data.results.forEach(result => {
            const podName = result.pod_name;
            const podStatus = result.pod_aws_status;
            let msg = "";
            let msgStatus = "";

            if (podStatus === 2) {
              msg = `Pod "${podName}" was successfully ingested and is ready to be scheduled.`;
              msgStatus = `success`;
            } else if (podStatus === 3) {
              msg = `Pod "${podName}" has Failed and was not ingested.`;
              msgStatus = `danger`;
            }

            if (msg) {
              // displaying the Notification
              util.alertPodInfo(msg, msgStatus);
              // toggle the 'Notification seen' in the DB
              util.savePodNotification(result.pod, true, result.id);
            }
          });
        });
      }
      // check again in 60 seconds;
      const delay = 60;

      // start a new timeout;
      reoccurringTimeout.current = setTimeout(() => {
        getPodsTranscodingNotifications();
      }, delay * 1000);
    }

    // updating the state from the parent, if we have any data, and if it changed
    // fallback to default values if the parent doesn't supply values for all variables
    if (!_.isEmpty(props)) {
      const userUpdated = props.userData.email || props.userData.name || null;
      const stationUpdated = props.userData.current_station || null;
      const stations = props.userData.stations || [];
      const permissions = {
        superuser: props.userData.is_superuser,
        groups: props.userData.groups
      };

      // delay the pods transcoding notification, on Initial (react) page load
      if (stationUpdated && !initialTimeout.current) {
        initialTimeout.current = setTimeout(() => {
          getPodsTranscodingNotifications();
        }, 60000);
      }

      // updating the state only if it changed
      if (userUpdated !== user || stationUpdated !== station) {
        setUser(userUpdated);
        setStation(stationUpdated);
        setStations(stations);
        setPermissions(permissions);
      }
    }
  }, [props, user, station]);

  // using a separate useEffect dedicated solely for cleanup (not to be affected by
  // other state variables from the other useState's dependencyArray)
  // this will run only on the 'unmount' of the whole PageHeader (when refreshing
  // the page, signing-out, going to a different url,...):
  // [] - means this useEffect will only run on the 'mount' and 'unmount' of PageHeader
  // return... - means this part should run on the 'unmount' of PageHeader
  useEffect(()=>{
    // clear the timeout set by delayInitialPodsNotification
    // and by getPodsTranscodingNotifications
    return () => {
      clearTimeout(initialTimeout.current);
      clearTimeout(reoccurringTimeout.current);
    }
  }, []);

  // determine if the current path matches the link;
  const returnActiveClass = (path) => {
    return window.location.pathname.indexOf(path) === 0 ? `active` : null;
  }

  // return the list of stations;
  // filtered or not;
  const returnStationList = () => {
    if (stations.length > 0) {
      return stations.length > 1 && stations.map((stationItem) => {
        const index = stationItem.name.toLowerCase().indexOf(stationSearch.toLowerCase());

        // if the string was found, or;
        // if there are less than 10 stations to search through, just show them all;
        return index > -1 || stations.length < stationLength ? (
          <li key={ stationItem.id } className={ station && station.id === stationItem.id ? `active` : null }>
            <InaccessibleLink onClick={ () => props.onChangeStation(stationItem.id) }>
              { stationItem.name }
            </InaccessibleLink>
          </li>
        ) : null;
      });
    } else {
      return (
        <li className="dropdown-header">{ util.zeroStationsMessage }</li>
      );
    }
  }

  // return the divider after the user has selected a station;
  // or if the station search form is showing;
  const returnStationListDivider = () => {
    if (station && stations.length > 1) {
      return (
        <li role="separator" className="divider"></li>
      );
    }
  }

  // only show the search form if the user has enough to warrant a search;
  const returnStationSearchForm = () => {
    if (stations.length > stationLength) {
      return (
        <li>
          <div className="input-group">
            <span className="input-group-addon">
              <i className="fa fa-search" aria-hidden="true"></i>
            </span>
            <input
              type="text"
              className="form-control"
              value={ stationSearch }
              onChange={ (e) => {
                setStationSearch(e.target.value);
              } }
              placeholder="Filter station list&hellip;" />
          </div>
        </li>
      );
    }
  }

  const canAccessUserList = (permissions) => {
    if (Object.keys(permissions).length === 0) {
      return false;
    }
    if (permissions.superuser) {
      return true;
    }
    if (permissions.groups.map(g => g.name).includes('Station Admin')) {
      return true;
    }
    return false;
  }

  return (
    <header className="page-header">
      <nav className="navbar navbar-inverse">
        <div className="container-fluid">
          <div className="navbar-header">
            <button
              type="button"
              className="navbar-toggle collapsed"
              data-toggle="collapse"
              data-target="#page-header__collapse--account"
              aria-expanded="false"
            >
              <span className="sr-only">Toggle navigation</span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
            </button>

            <Link className="navbar-brand" to="/">
              <img src={headerLogo} alt="PBS" height="30"/>
              <span>KIDS 24/7 Interstitial Manager</span>
            </Link>
          </div>

          {
            user === null && station === null ? (
              <div className="collapse navbar-collapse" id="page-header__collapse--account">
                <ul className="nav navbar-nav navbar-right">
                  <li>
                    <a href={ `/auth/login/?next=${ util.returnScheduleWeekUrl(util.returnSunday(new Date())) }` }>
                      Login
                    </a>
                  </li>
                </ul>
              </div>
            ) : (
              <div className="collapse navbar-collapse" id="page-header__collapse--account">
                <ul className="nav navbar-nav navbar-right">
                  <li className="dropdown">
                    <InaccessibleLink
                      className="dropdown-toggle"
                      data-toggle="dropdown" role="button"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      <span className="page-header__user">{ station ? station.name : `Select a station` }</span>
                      <span className="caret"></span>
                    </InaccessibleLink>

                    <ul className="dropdown-menu page-header__dropdown--stations">
                      { permissions && canAccessUserList(permissions) && <li><Link to={ util.returnUserListUrl() }>Users</Link></li> }
                      { permissions && canAccessUserList(permissions) &&  <li><Link to={ util.returnInviteListUrl() }>Invites</Link></li> }
                      { station && <li><Link to={ util.returnChangeLogListUrl() }>Change Log</Link></li> }
                      { station && <li><Link to={ util.returnSettingsUrl() }>Settings</Link></li> }
                      { returnStationSearchForm() }
                      { returnStationListDivider() }
                      { returnStationList() }
                    </ul>
                  </li>

                  <li className="dropdown">
                    <InaccessibleLink
                      className="dropdown-toggle"
                      data-toggle="dropdown"
                      role="button"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      <span className="page-header__user">{ user }</span>
                      <span className="caret"></span>
                    </InaccessibleLink>

                    <ul className="dropdown-menu">
                      <li><a href="/auth/logout/">Logout</a></li>
                    </ul>
                  </li>
                </ul>
              </div>
            )
          }
        </div>
      </nav>

      {
        station !== null ? (
          <nav className="navbar navbar-default">
            <div className="container-fluid">
              <div className="navbar-header">
                <button
                  type="button"
                  className="navbar-toggle collapsed"
                  data-toggle="collapse"
                  data-target="#page-header__collapse--nav"
                  aria-expanded="false"
                >
                  <span className="sr-only">Toggle navigation</span>
                  <span className="icon-bar"></span>
                  <span className="icon-bar"></span>
                  <span className="icon-bar"></span>
                </button>
              </div>

              <div className="collapse navbar-collapse" id="page-header__collapse--nav">
                <ul className="nav navbar-nav">
                  <li className={ returnActiveClass(`/livestream`) }>
                    <Link to={ util.returnLivestreamUrl() }>Livestream</Link>
                  </li>
                </ul>

                <ul className="nav navbar-nav">
                  <li className="hidden-sm"><span className="page-header__nav-label">Scheduling:</span></li>

                  <li className={ returnActiveClass(`/rule`) }>
                    <Link to={ util.returnRuleListUrl() }>Rules</Link>
                  </li>

                  <li className={ returnActiveClass(`/schedule`) }>
                    <Link to={ util.returnScheduleWeekUrl(util.returnSunday(new Date())) }>Schedule</Link>
                  </li>

                  <li className={ returnActiveClass(`/reports`) }>
                    <Link to={ util.returnReportsUrl() }>Reporting</Link>
                  </li>
                </ul>

                <ul className="nav navbar-nav">
                  <li className="hidden-sm"><span className="page-header__nav-label">Interstitials:</span></li>

                  {util.spotsAvailable() &&
                  <li className={ returnActiveClass(`/spots`) }>
                    <Link to={ util.returnSpotListUrl() }>Spots</Link>
                  </li>}

                  <li className={ returnActiveClass(`/pod`) }>
                    <Link to={ util.returnPodListUrl() }>Pods</Link>
                  </li>

                  <li className={ returnActiveClass(`/campaign`) }>
                    <Link to={ util.returnCampaignListUrl() }>Campaigns</Link>
                  </li>
                </ul>

                <ul className="nav navbar-nav navbar-right">

                  <li><ConflictCount /></li>

                  <li className="dropdown">
                    <InaccessibleLink
                      className="dropdown-toggle"
                      data-toggle="dropdown"
                      role="button"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      Create New
                      <span className="caret"></span>
                    </InaccessibleLink>

                    <ul className="dropdown-menu">
                      {util.spotsAvailable() &&
                      <li><Link to={ util.returnSpotFormUrl() }>Spot</Link></li>}
                      <li><Link to={ util.returnPodFormUrl() }>Pod</Link></li>
                      <li><Link to={ util.returnCampaignFormUrl() }>Campaign</Link></li>
                      <li><Link to={ util.returnRuleFormUrl() }>Rule</Link></li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        ) : null
      }
    </header>
  );
}

PageHeader.propTypes = {
  userData: PropTypes.object,
  onChangeStation: PropTypes.func
};
